import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';
import {
  func, string, bool, oneOfType, arrayOf, element, number
} from 'prop-types';
import { getTranslation, withLang } from '../../../../i18n';
import {
  questionTypeValues,
  schemaPartItemTypeValues,
  staticTextTypeValues,
  SurveySchemaPartItemEditorType
} from '../../../../constants/types';
import { removeSchemaPartItemAction, updateSchemaPartItemAction } from '../../../../actions';
import Question from '../Question';
import QuestionGroup from '../QuestionGroup';
import Heading from '../Heading';
import InstructionText from '../InstructionText';
import SurveyPart from '../../../Survey/SurveyPartItem';
import itemsStyles from '../SurveyPartItems/surveyPartItems.module.css';

export const ItemHeader = ({
  isOpen, titleTranslationKey, preview, toggleOpen
}) => {
  const isOpenStyle = isOpen ? itemsStyles.open : '';
  return (
    <div className={`${itemsStyles.header} ${itemsStyles.elementHeader}`}>
      <button type="button" className={isOpenStyle} onClick={() => toggleOpen(!isOpen)}>
        <Translate id={titleTranslationKey} />
      </button>
      <span className={itemsStyles.previewText}>{preview}</span>
    </div>
  );
};

export const ItemBody = ({ isOpen, children }) => (
  <div className={`${itemsStyles.elementContent} ${isOpen && itemsStyles.open}`}>
    {children}
  </div>
);

export const itemTitleTranslationKey = (item) => {
  if (item.type === schemaPartItemTypeValues.QUESTION) {
    switch (item.question.type) {
      case questionTypeValues.TEXT_FIELD:
        return 'surveyParts.itemTitles.textField';
      case questionTypeValues.DROPDOWN:
        return 'surveyParts.itemTitles.dropdown';
      case questionTypeValues.RADIO_BUTTON_GROUP:
        return 'surveyParts.itemTitles.radioButtonGroup';
      case questionTypeValues.CHECKBOX_GROUP:
        return 'surveyParts.itemTitles.checkboxGroup';
      default:
        return null;
    }
  } else if (item.type === schemaPartItemTypeValues.QUESTION_GROUP) {
    return 'surveyParts.itemTitles.likert';
  } else if (item.type === schemaPartItemTypeValues.STATIC_TEXT) {
    switch (item.staticText.type) {
      case staticTextTypeValues.HEADING:
        return 'surveyParts.itemTitles.heading';
      case staticTextTypeValues.INSTRUCTION_TEXT:
        return 'surveyParts.itemTitles.instructionText';
      default:
        return null;
    }
  }
  return null;
};

const htmlDecode = (input) => {
  if (!input) {
    return '';
  }
  const doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.documentElement.textContent;
};

export const itemTitlePreview = (item, translate, lang) => {
  switch (item.type) {
    case schemaPartItemTypeValues.QUESTION:
      return getTranslation(item.question.text, lang);
    case schemaPartItemTypeValues.QUESTION_GROUP: {
      const count = item.questionGroup.questions.length;
      return `${count} ${(count === 1)
        ? translate('surveyParts.nQuestionsSingular')
        : translate('surveyParts.nQuestionsPlural')
      }`;
    }
    case schemaPartItemTypeValues.STATIC_TEXT:
      return htmlDecode(getTranslation(item.staticText.text, lang));
    default:
      return null;
  }
};

class SurveyPartItem extends Component {
  constructor(props) {
    super(props);

    this.renderItemBody = this.renderItemBody.bind(this);
    this.renderPreviewBody = this.renderPreviewBody.bind(this);
    this.onLocalizedInputChange = this.onLocalizedInputChange.bind(this);
    this.onLocalizedRTEChange = this.onLocalizedRTEChange.bind(this);
  }

  onLocalizedInputChange(lang) {
    const itemIndex = this.props.item.order - 1;
    return (event) => this.props.dispatchUpdateItem(itemIndex, {
      staticText: {
        [event.target.name]: { [lang]: event.target.value }
      }
    });
  }

  onLocalizedRTEChange(lang, name) {
    const itemIndex = this.props.item.order - 1;
    return (value) => this.props.dispatchUpdateItem(itemIndex, {
      staticText: {
        [name]: { [lang]: value }
      }
    });
  }

  titlePreview() {
    const { item, lang, translate } = this.props;
    return itemTitlePreview(item, translate, lang);
  }

  renderItemBody() {
    const staticTextToRender = (item) => {
      switch (item.staticText.type) {
        case staticTextTypeValues.HEADING:
          return (
            <Heading
              item={item}
              onRemove={this.props.onRemove}
              onLocalizedHeadingInputChange={this.onLocalizedInputChange}
              lockedFromEditing={this.props.lockedFromEditing}
            />
          );
        case staticTextTypeValues.INSTRUCTION_TEXT:
          return (
            <InstructionText
              item={item}
              onRemove={this.props.onRemove}
              onLocalizedInstructionTextInputChange={this.onLocalizedRTEChange}
              lockedFromEditing={this.props.lockedFromEditing}
            />
          );
        default:
          return null;
      }
    };

    const { item } = this.props;
    const toRender = () => {
      switch (item.type) {
        case schemaPartItemTypeValues.QUESTION:
          return (
            <Question
              item={item}
              onRemove={this.props.onRemove}
              lockedFromEditing={this.props.lockedFromEditing}
            />
          );
        case schemaPartItemTypeValues.QUESTION_GROUP:
          return (
            <QuestionGroup
              item={item}
              onRemove={this.props.onRemove}
              nextFreeQuestionCode={this.props.nextFreeQuestionCode}
              lockedFromEditing={this.props.lockedFromEditing}
            />
          );
        case schemaPartItemTypeValues.STATIC_TEXT:
          return staticTextToRender(item);
        default:
          return null;
      }
    };

    return (
      <ItemBody isOpen={this.props.isOpen}>
        {toRender()}
      </ItemBody>
    );
  }

  renderPreviewBody() {
    return (
      <ItemBody isOpen={this.props.isOpen}>
        <SurveyPart
          schemaPartItem={this.props.item}
          questionOrderNumById={this.props.questionOrderNumById}
          lang={this.props.lang}
          handleResponseChange={() => null}
          getQuestionAnswer={() => null}
          getErrorState={() => null}
          translate={this.props.translate}
        />
      </ItemBody>
    );
  }

  render() {
    return (
      <div>
        {this.props.withHeader && (
        <ItemHeader
          isOpen={this.props.isOpen}
          titleTranslationKey={itemTitleTranslationKey(this.props.item)}
          preview={this.titlePreview()}
          toggleOpen={this.props.onToggleVisible}
        />
        )}
        {(this.props.readOnly) ? this.renderPreviewBody() : this.renderItemBody()}
      </div>
    );
  }
}

SurveyPartItem.defaultProps = {
  nextFreeQuestionCode: null,
  readOnly: false,
  questionOrderNumById: [],
  withHeader: true
};

SurveyPartItem.propTypes = {
  item: SurveySchemaPartItemEditorType.isRequired,
  isOpen: bool.isRequired,
  onRemove: func.isRequired,
  nextFreeQuestionCode: func,
  onToggleVisible: func.isRequired,
  lang: string.isRequired,
  translate: func.isRequired,
  dispatchUpdateItem: func.isRequired,
  readOnly: bool,
  questionOrderNumById: arrayOf(number),
  lockedFromEditing: bool.isRequired,
  withHeader: bool
};

ItemHeader.defaultProps = {
  preview: ''
};

ItemHeader.propTypes = {
  isOpen: bool.isRequired,
  titleTranslationKey: string.isRequired,
  preview: string,
  toggleOpen: func.isRequired
};

ItemBody.propTypes = {
  isOpen: bool.isRequired,
  children: oneOfType([arrayOf(element), element]).isRequired
};

const mapDispatchToProps = (dispatch) => ({
  dispatchUpdateItem: (index, item) =>
    dispatch(updateSchemaPartItemAction(index, item)),
  dispatchRemoveItem: (index) =>
    dispatch(removeSchemaPartItemAction(index))
});

export default withLang(connect(null, mapDispatchToProps)(SurveyPartItem));
